import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Tag, User } from "lucide-react";
import Seo from "../components/Seo";
import RetreatInquiryForm from "../components/RetreatInquiryForm";
import FreeConsultationSection from "../components/FreeConsultationSection";
import TeacherScheduleBlock from "../components/TeacherScheduleBlock";
import CertificationsSection from "../components/CertificationsSection";
import PhotoGallery from "../components/PhotoGallery";
import { fetchTeacherBySlug } from "../services/api";
import { getOfferingListingPath } from "../lib/offeringRoutes";
import { ONLINE_COURSE, PRENATAL_YOGA } from "../lib/courseTypes";
import { buildTeacherScheduleLines } from "../lib/teacherScheduleLines";
import { formatScheduleCompact } from "../lib/scheduleDisplay";
import { getTeacherImage } from "../lib/teacherImages";

function getPageTitle(offerings, listingType, teacherName) {
  const titles = [...new Set(offerings.map((offering) => offering.title).filter(Boolean))];
  if (titles.length === 1) return titles[0];
  if (listingType === PRENATAL_YOGA) return "Prenatal Yoga Classes";
  return teacherName || "Online Yoga Classes";
}

function resolveOfferingPriceInr(offering) {
  const fromInr = Number(offering?.price_inr);
  if (Number.isFinite(fromInr) && fromInr > 0) return Math.round(fromInr);
  const currency = String(offering?.currency || "INR").toUpperCase();
  const fromPrice = Number(offering?.price);
  if (currency === "INR" && Number.isFinite(fromPrice) && fromPrice > 0) {
    return Math.round(fromPrice);
  }
  return null;
}

function toPayableOfferings(offerings) {
  return (Array.isArray(offerings) ? offerings : [])
    .map((offering) => {
      const priceInr = resolveOfferingPriceInr(offering);
      if (priceInr == null) return null;
      const schedule =
        formatScheduleCompact(offering.schedule_json, offering.schedule_summary) || "";
      return {
        offering_key: offering.offering_key,
        slug: offering.slug || "",
        title: offering.title || "Class",
        schedule,
        price_inr: priceInr,
      };
    })
    .filter(Boolean);
}

function TeacherInstructorDetailPage({ listingType = ONLINE_COURSE }) {
  const { slug } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const listingPath = getOfferingListingPath(listingType);
  const listingLabel = listingType === PRENATAL_YOGA ? "Prenatal Yoga" : "Online Courses";

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchTeacherBySlug(slug)
      .then((data) => setTeacher(data))
      .catch(() => setError("Could not load class details. Please try again."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  const offerings = (teacher?.offerings || []).filter((item) => item.listing_type === listingType);

  if (error || !teacher || offerings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-gray-600 mb-4">{error || "Class not found."}</p>
          <Link to={listingPath} className="text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">
            <ArrowLeft size={16} /> Back to {listingLabel}
          </Link>
        </div>
      </div>
    );
  }

  const location = [teacher.city, teacher.country].filter(Boolean).join(", ") || "India";
  const pageTitle = getPageTitle(offerings, listingType, teacher.teacher_name);
  const scheduleLines = buildTeacherScheduleLines(offerings, { includePrices: true });
  const primaryOffering = offerings[0];
  const payableOfferings = toPayableOfferings(offerings);
  const categories = [...new Set(offerings.map((offering) => offering.category).filter(Boolean))];
  const galleryImages = Array.isArray(teacher.gallery_images) ? teacher.gallery_images : [];
  const teacherPhoto = getTeacherImage({
    teacher_slug: teacher.slug,
    teacher_id: teacher.teacher_id,
    profile_image: teacher.profile_image,
  });
  const canonicalPath = `${listingPath}/instructor/${slug}`;
  const pageDescription = `${pageTitle} with ${teacher.teacher_name} in ${location}. Live online classes with flexible schedules. Enquire to book your spot.`;
  const inquiryRetreat = {
    retreat_key: primaryOffering.offering_key,
    retreat_name: `${pageTitle} with ${teacher.teacher_name}`,
    teacher_name: teacher.teacher_name,
    slug: primaryOffering.slug,
    location,
    source: "yoga_teachers",
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <Seo
        title={`${pageTitle} with ${teacher.teacher_name}`}
        description={pageDescription}
        canonicalPath={canonicalPath}
        ogImagePath={teacherPhoto}
      />

      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-900 to-gray-900 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_45%)]" />
        <div className="relative max-w-5xl mx-auto px-4 py-10 md:py-14">
          <Link
            to={listingPath}
            className="inline-flex items-center gap-1 text-sm text-emerald-100 hover:text-white mb-8"
          >
            <ArrowLeft size={14} /> Back to {listingLabel}
          </Link>

          <div className="flex flex-col md:flex-row items-center md:items-end gap-8">
            <div className="w-36 h-36 md:w-44 md:h-44 rounded-3xl overflow-hidden bg-white/10 ring-4 ring-white/20 shadow-2xl shrink-0">
              <img
                src={teacherPhoto}
                alt={teacher.teacher_name}
                className="w-full h-full object-cover object-top"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="flex-1 text-center md:text-left pb-2">
              <p className="text-emerald-200 text-xs font-bold uppercase tracking-[0.25em] mb-3">
                {listingType}
              </p>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-3">
                {teacher.teacher_name}
              </h1>
              <p className="text-emerald-50/90 text-base md:text-lg mb-3">{pageTitle}</p>
              <p className="flex items-center justify-center md:justify-start gap-2 text-sm text-emerald-100/90">
                <MapPin size={15} className="text-emerald-300" />
                {location}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <Clock size={18} className="text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Classes</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {offerings.length} live {offerings.length === 1 ? "session" : "sessions"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Tag size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</p>
                    <p className="text-sm font-semibold text-gray-800">{listingType}</p>
                  </div>
                </div>
              </div>
            </div>

            {categories.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Tag size={14} /> Course details
                </h2>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100">
                    {listingType}
                  </span>
                  {categories.map((category) => (
                    <span
                      key={category}
                      className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full border border-emerald-100"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <CertificationsSection certifications={teacher.certifications} compact />

            <TeacherScheduleBlock lines={scheduleLines} cardTitle={pageTitle} />

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <User size={18} className="text-emerald-500" /> About the teacher
              </h2>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shrink-0">
                  <img
                    src={teacherPhoto}
                    alt=""
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{teacher.teacher_name}</p>
                  {teacher.headline && (
                    <p className="text-gray-600 text-sm leading-relaxed mt-1">{teacher.headline}</p>
                  )}
                </div>
              </div>
              {teacher.bio && (
                <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-4">{teacher.bio}</p>
              )}
              <Link
                to={`/teachers/${teacher.slug}`}
                className="inline-flex items-center gap-1 text-sm font-bold text-emerald-600 hover:text-emerald-700"
              >
                View full teacher profile
              </Link>
            </div>

            {galleryImages.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Gallery</h2>
                <PhotoGallery images={galleryImages} />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Pricing</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Class fees are listed next to each time slot. Prefer a custom time? Choose{" "}
                <span className="font-medium text-gray-800">Price &amp; schedule on request</span>{" "}
                and enquire below.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Instructor</p>
              <p className="text-sm font-semibold text-gray-900">{teacher.teacher_name}</p>
              <p className="text-xs text-gray-500 mt-1">{location}</p>
            </div>
          </div>
        </div>

        <FreeConsultationSection className="mt-12" context={inquiryRetreat} />

        <section className="mt-12 scroll-mt-28 relative">
          <div id="enquire" className="absolute -top-28" aria-hidden="true" />
          <div id="book-enquire" className="absolute -top-28" aria-hidden="true" />
          <RetreatInquiryForm
            variant="section"
            retreat={inquiryRetreat}
            offerings={payableOfferings}
          />
        </section>
      </div>
    </div>
  );
}

export default TeacherInstructorDetailPage;
